import { CheckSquareFilled, MoreOutlined, PlusOutlined, UserOutlined } from '@ant-design/icons';
import Icon from '@ant-design/icons/lib/components/Icon';
import { Avatar, Button, message } from 'antd';
import { ItemType } from 'antd/es/menu/interface';
import React, { useContext, useState } from 'react';
import { flushSync } from 'react-dom';
import { ExpandDown } from '../../../assets/CustomIcons/ExpandDown';
import { ExpandRight } from '../../../assets/CustomIcons/ExpandRight';
import FlagPriority from '../../../assets/icons/flag-priority.svg?react';
import FlagPriorityWhite from '../../../assets/icons/FlagPriorityWhite.svg?react';
import { getUserProfilePictureUrl } from '../../../common/userHelper';
import { ModalContext, ModalType } from '../../../context/ModalContext';
import { useGetActualSpaceId } from '../../../serverState/workspace';
import { SpaceSettingsTab } from '../../../pages/Space/SpaceSettingsModal/SpaceSettingsModal';
import { useSpaceSettingsStore } from '../../../stores/spaceSettingsStore';
import { useViewContext } from '../../../pages/Space/Context/ViewContext';
import { Epic } from '../../../types/Epic';
import { SpaceLabel as Label } from '../../../types/TaskLabel';
import { defaultTaskStatuses, TaskStatus, TaskStatusGroup } from '../../../types/TaskStatus';
import { TaskType, TaskTypeUtils } from '../../../types/TaskType';
import { User } from '../../../types/User';
import { WorkspaceEstimationType, WorkspaceFramework } from '../../../types/Workspace';
import { WorkspaceViewEntityType } from '../../../types/WorkspaceView';
import { Column } from '../../../types/WorkspaceViewColumn';
import { GroupItem } from '../../Backlog/TaskGroupSection/TaskGroupSection';
import { EpicHeader } from '../../Backlog/TaskGroupSection/components/EpicHeader';
import { GroupProgressIndicator } from '../../Backlog/TaskGroupSection/components/GroupProgressIndicator';
import {
  flagWidth,
  PriorityFlagPropsMap,
  PriorityOptions,
  TaskEstimateLongMapping,
} from '../../TaskTable/helpers/helper';
import BaseDropdown from '../../UI/Dropdown/BaseDropdown';
import { TaskTypeLabel } from '../../UI/Dropdown/TaskTypeDropdown';
import LabelChip, { LabelChipType } from '../../UI/LabelChip';
import { SprintStatusLabel } from '../../UI/SprintStatusLabel';
import StatusLabel, { StatusComponentType } from '../../UI/StatusLabel';
import { TaskEstimateComponentType, TaskEstimateLabel } from '../../UI/TaskEstimateLabel';
import CustomTooltip from '../../UI/Tooltip/CustomTooltip';
import { deleteTasks } from '../../../api/workspace';
import { queryClient } from '../../../queryClient';
import { TaskTableDataType } from '../../TaskTable/types/TaskTableTypes';

interface VirtualizedGroupHeaderProps {
  group: GroupItem;
  groupBy: string;
  stats: {
    completed: number;
    total: number;
    percent: number;
  };
  isExpanded: boolean;
  onToggleExpand: () => void;
  framework?: WorkspaceFramework;
  estimationType?: WorkspaceEstimationType;
  tasks: TaskTableDataType[];
  groupId: string;
  isHovered: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  showMultiselect: boolean;
  allTasksSelected: boolean;
  hasAnySelected: boolean;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  hasRowSelection?: boolean;
  hideGroupActions?: boolean;
}

export const VirtualizedGroupHeader: React.FC<VirtualizedGroupHeaderProps> = ({
  group,
  groupBy,
  stats,
  isExpanded,
  onToggleExpand,
  framework,
  estimationType = WorkspaceEstimationType.storyPoints,
  tasks,
  groupId,
  isHovered,
  onMouseEnter,
  onMouseLeave,
  showMultiselect,
  allTasksSelected,
  hasAnySelected,
  onSelectAll,
  onDeselectAll,
  hasRowSelection,
  hideGroupActions,
}) => {
  const viewContext = useViewContext();
  const { openModal } = useContext(ModalContext);
  const activeSpaceId = useGetActualSpaceId();
  const requestOpenSpaceSettings = useSpaceSettingsStore((state) => state.requestOpen);
  const [messageApi, contextHolder] = message.useMessage();
  const [deleteOpen, setDeleteOpen] = useState(false);

  const onCreateTask = (e: React.MouseEvent) => {
    e.stopPropagation();
    let modalProps = {};
    if (groupBy === Column.epic) {
      modalProps = { epicId: group?.id };
    } else if (groupBy === Column.taskType) {
      modalProps = { taskType: group as TaskType };
    } else if (groupBy === Column.assignee) {
      modalProps = { assignee: group as User };
    } else if (groupBy === Column.status) {
      modalProps = { status: group as TaskStatus };
    } else if (groupBy === Column.estimate) {
      modalProps = { estimate: group as number };
    }
    if (viewContext?.view?.entityType === WorkspaceViewEntityType.epic) {
      modalProps = { ...modalProps, taskType: TaskType.epic };
    }
    openModal(ModalType.CreateItem, modalProps);
  };

  const getRowMenuItemsFor = (groupType: string): ItemType[] => {
    switch (groupType) {
      case Column.epic:
        return [
          {
            key: 'renameEpic',
            label: 'Rename Epic',
          },
          {
            type: 'divider',
          },
          {
            key: 'deleteEpic',
            label: <span>Delete Epic</span>,
          },
        ];
      case Column.status:
        return [
          {
            key: 'manageStatuses',
            label: 'Manage Statuses',
          },
        ];
      default:
        return [];
    }
  };

  const handleDelete = async (deleteSubtasks: boolean) => {
    try {
      const epic = group as Epic;
      if (!epic) return;
      const allTasksInGroup = [...tasks, ...tasks.flatMap((task) => task.subtasks || [])].filter(
        (task) => task.epicId === epic.id
      );
      const tasksToDelete: number[] = [epic.id, ...(deleteSubtasks ? allTasksInGroup : []).map((task) => task.id)];
      await deleteTasks(epic.workspaceId, tasksToDelete);

      setDeleteOpen(false);

      setTimeout(async () => {
        await queryClient.setQueryData(['epics', epic.workspaceId], (oldDataSource: Epic[]) => {
          return oldDataSource.filter((item) => !tasksToDelete.includes(item.id));
        });
        await queryClient.setQueryData(['tasks', epic.workspaceId], (oldDataSource: TaskTableDataType[]) => {
          let newDataSource = oldDataSource.filter((item) => !tasksToDelete.includes(item.id));
          if (!deleteSubtasks) {
            newDataSource = newDataSource.map((task) => {
              if (task.epicId === epic.id) {
                return {
                  ...task,
                  epicId: null,
                };
              }
              return task;
            });
          }
          return newDataSource;
        });
        void messageApi.success('Epic deleted successfully.');
      }, 50);
    } catch (e) {
      console.error(e);
      void messageApi.error('Failed to delete epic');
    }
  };

  const rowMenuItems: ItemType[] = [
    {
      key: 'expandGroup',
      label: isExpanded ? 'Collapse Group' : 'Expand Group',
    },
    ...getRowMenuItemsFor(groupBy),
  ];

  const handleMenuClick = (e: any) => {
    e.domEvent?.stopPropagation();
    if (e.key === 'expandGroup') {
      onToggleExpand();
    }
    if (e.key === 'deleteEpic') {
      setDeleteOpen(true);
    }
    if (e.key === 'renameEpic') {
      setTimeout(() => {
        flushSync(() => {
          const element = document.querySelector(`[epic-name-id='${Number(group?.id)}']`) as HTMLElement;
          element?.click();
        });
        const newEpicInput = document.querySelector(`[epic-name-input-id='${Number(group?.id)}']`) as HTMLInputElement;
        newEpicInput?.focus();
        newEpicInput?.select();
      }, 300);
    }
    if (e.key === 'manageStatuses') {
      if (activeSpaceId) {
        requestOpenSpaceSettings(SpaceSettingsTab.STATUSES);
      } else {
        openModal(ModalType.EditStatuses);
      }
    }
  };

  return (
    <div
      className="h-[50px] flex items-center w-full bg-white border-b border-neutral-200"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {contextHolder}
      {/* The bar spans the full row width so its background always covers the
          horizontally-scrolled columns. Only the label/actions are pinned to the
          viewport-left via a nested sticky — pinning the whole bar made it (being
          narrower than the scrollable content) drift left at the end of the scroll (#2561). */}
      <div className="flex items-center sticky left-0 z-[1]">
        <div className="flex items-center px-2">
          {/* Dropdown menu — hidden in views like All Tasks, but keep the space so the layout matches other modules */}
          {hideGroupActions ? (
            <div className="ml-2" style={{ height: '20px', width: '20px' }} />
          ) : (
            <div className={`flex items-center ml-2 ${isHovered ? '' : 'opacity-0 pointer-events-none'}`}>
              <BaseDropdown
                menu={{
                  items: rowMenuItems,
                  onClick: handleMenuClick,
                }}
                trigger={['click']}
              >
                <Button
                  icon={<MoreOutlined />}
                  style={{ height: '20px', width: '20px', padding: 0 }}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                />
              </BaseDropdown>
            </div>
          )}

          {/* Select all checkbox */}
          {showMultiselect && (
            <div
              className={`flex items-center ml-2 ${!allTasksSelected && !isHovered && !hasAnySelected ? 'invisible' : ''}`}
            >
              {allTasksSelected ? (
                <CheckSquareFilled
                  style={{ fontSize: '16px' }}
                  className="text-blue-500 cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeselectAll();
                  }}
                />
              ) : (
                <div
                  className="h-4 w-4 rounded-sm border-box border border-solid border-neutral-600 hover:border-blue-500 cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectAll();
                  }}
                />
              )}
            </div>
          )}

          {/* Expand/Collapse button */}
          <Button
            className={`${hasRowSelection ? 'ml-[6px]' : 'ml-2'} text-neutral-900 flex items-center justify-center`}
            style={{ height: '20px', width: '20px', padding: 0 }}
            onClick={onToggleExpand}
          >
            {isExpanded && <Icon component={ExpandDown} />}
            {!isExpanded && <Icon component={ExpandRight} />}
          </Button>
        </div>

        <div className="w-[500px] flex items-center gap-1">
          {/* Group title based on groupBy type */}
          {groupBy === Column.epic && isObjectWithKey(group, 'title') && <EpicHeader epic={group} />}
          {groupBy === Column.taskType && typeof group === 'string' && (
            <div className="rounded">
              <TaskTypeLabel type={group as TaskType} size={'small'} />
            </div>
          )}
          {groupBy === Column.epic && !isObjectWithKey(group, 'title') && (
            <GroupTitleWithTooltip
              group={group}
              groupBy={groupBy}
              framework={framework}
              estimationType={estimationType}
            />
          )}
          {groupBy !== Column.epic && (
            <GroupTitleWithTooltip
              group={group}
              groupBy={groupBy}
              framework={framework}
              estimationType={estimationType}
            />
          )}

          {/* Progress indicator */}
          <GroupProgressIndicator
            groupBy={groupBy}
            completedTasksCount={stats.completed}
            totalTasksCount={stats.total}
            taskCompletionPercent={stats.percent}
          />

          {/* Add task button */}
          <div className={`${isHovered ? '' : 'opacity-0 pointer-events-none'}`}>
            <CustomTooltip title="Add Task">
              <Button
                type="link"
                className="h-6 ml-1 p-0 bg-white rounded shadow border border-neutral-300 hover:border-brand-taskonio-green justify-start items-center gap-1 inline-flex"
                onClick={onCreateTask}
              >
                <PlusOutlined className="text-neutral-900 hover:text-brand-taskonio-green p-1" />
              </Button>
            </CustomTooltip>
          </div>
        </div>
      </div>
    </div>
  );
};

const getGroupTooltipText = (group: GroupItem, groupBy: string): string | undefined => {
  switch (groupBy) {
    case Column.assignee:
      if (isObjectWithKey(group, 'email')) {
        const user = group as unknown as User;
        return user.fullName || user.email;
      }
      return 'No Owner Assigned';
    case Column.epic:
      return isObjectWithKey(group, 'title') ? (group as Epic).title : 'No Epic Assigned';
    default:
      return undefined;
  }
};

const GroupTitleWithTooltip = ({
  group,
  groupBy,
  framework,
  estimationType,
}: {
  group: GroupItem;
  groupBy: string;
  framework?: WorkspaceFramework;
  estimationType: WorkspaceEstimationType;
}) => {
  const title = getGroupTitle(group, groupBy, framework, estimationType);
  const tooltipText = typeof title === 'string' ? title : getGroupTooltipText(group, groupBy);
  return (
    <CustomTooltip title={tooltipText}>
      <div className="cursor-default text-brand-grey-dark text-base font-semibold leading-tight pl-1 pr-1 ml-0.5 max-w-[300px] rounded truncate overflow-hidden whitespace-nowrap">
        {title}
      </div>
    </CustomTooltip>
  );
};

const isObjectWithKey = (value: unknown, key: string): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && key in value;

const getGroupTitle = (
  groupItem: GroupItem,
  groupBy: string,
  framework?: WorkspaceFramework,
  estimationType: WorkspaceEstimationType = WorkspaceEstimationType.storyPoints
) => {
  switch (groupBy) {
    case Column.taskType: {
      return typeof groupItem === 'string' ? (
        <span className="flex items-center gap-1 ml-[-4px]">{TaskTypeUtils.getTitle(groupItem as TaskType)}</span>
      ) : (
        'No Type'
      );
    }
    case Column.epic:
      return isObjectWithKey(groupItem, 'title') ? (groupItem as Epic).title : 'No Epic Assigned';
    case Column.assignee: {
      if (isObjectWithKey(groupItem, 'email')) {
        const user = groupItem as unknown as User;
        return (
          <div className="flex items-center min-w-0">
            <Avatar
              key={user.id}
              size={20}
              className="flex-shrink-0 pointer-events-none select-none"
              {...(user.profilePicturePath ? { src: getUserProfilePictureUrl(user.profilePicturePath) } : {})}
              {...(!user.profilePicturePath
                ? {
                    icon: <UserOutlined />,
                  }
                : {})}
            ></Avatar>
            <span className="leading-6 text-center ml-1 truncate overflow-hidden whitespace-nowrap" key={user.email}>
              {user.fullName || user.email}
            </span>
          </div>
        );
      }
      return 'No Owner Assigned';
    }
    case Column.status: {
      if (isObjectWithKey(groupItem, 'color') && isObjectWithKey(groupItem, 'group')) {
        const status = groupItem as TaskStatus;
        return (
          <StatusLabel
            color={status.color}
            title={status.title}
            group={status.group}
            component={StatusComponentType.TableHeader}
          />
        );
      }
      return 'No Status';
    }
    case Column.estimate: {
      if (
        typeof groupItem === 'number' &&
        Object.keys(TaskEstimateLongMapping(estimationType)).includes(groupItem.toString())
      ) {
        return (
          <TaskEstimateLabel
            estimate={groupItem}
            component={TaskEstimateComponentType.table}
            estimationType={estimationType}
          />
        );
      }
      return 'Not Estimated';
    }
    case Column.priority: {
      const priority = groupItem as PriorityOptions;
      if (priority && PriorityFlagPropsMap[priority]) {
        const { colorFill, label } = PriorityFlagPropsMap[priority];
        return (
          <div className="flex items-center w-[120px]">
            {priority === PriorityOptions.NoPriority ? (
              <FlagPriorityWhite style={{ width: flagWidth }} />
            ) : (
              <FlagPriority style={{ width: flagWidth }} fill={colorFill} />
            )}
            <span style={{ color: colorFill }}>{label}</span>
          </div>
        );
      }
      return 'No Priority';
    }
    case Column.startDate: {
      return typeof groupItem === 'string' ? groupItem : 'No Start Date Assigned';
    }
    case Column.deadline: {
      return typeof groupItem === 'string' ? groupItem : 'No Target Date Assigned';
    }
    case Column.sprintStatus: {
      if (typeof groupItem === 'string' && ['triage', 'planning', 'sprint', 'kanban', 'none'].includes(groupItem)) {
        return (
          <SprintStatusLabel
            sprintStatus={groupItem as 'triage' | 'planning' | 'sprint' | 'kanban' | 'none'}
            component={'table'}
          />
        );
      }
      return <SprintStatusLabel sprintStatus={'none'} component={'table'} />;
    }
    case Column.label: {
      if (isObjectWithKey(groupItem, 'title') && isObjectWithKey(groupItem, 'color')) {
        return <LabelChip label={groupItem as Label} type={LabelChipType.TableHeader} />;
      }
      return 'No Label';
    }
  }
  return '';
};
